import { supabase } from '../lib/supabase';

export interface CompanyInfo {
  name: string;
  logo_url: string | null;
  slug: string;
  contact_email: string | null;
}

/**
 * Met à jour le nom, le slug et/ou l'email de contact d'une company.
 * Accessible uniquement par company_admin et super_admin (RLS).
 */
export async function updateCompanyInfo(
  companyId: string,
  data: { name?: string; slug?: string; contact_email?: string }
): Promise<void> {
  const { error } = await supabase
    .from('companies')
    .update(data)
    .eq('id', companyId);

  if (error) throw new Error(error.message);
}

/**
 * Met à jour uniquement contact_email d'une company.
 * Utilisé notamment après la création d'un company_admin pour pré-remplir l'email.
 */
export async function updateCompanyContactEmail(
  companyId: string,
  email: string
): Promise<void> {
  const { error } = await supabase
    .from('companies')
    .update({ contact_email: email })
    .eq('id', companyId);

  if (error) throw new Error(error.message);
}

/**
 * Upload un logo dans le bucket Supabase Storage "company-logos"
 * puis met à jour le champ logo_url de la company.
 * Retourne la nouvelle URL publique.
 */
export async function uploadCompanyLogo(
  companyId: string,
  file: File
): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'png';
  const path = `${companyId}/logo.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('company-logos')
    .upload(path, file, { upsert: true, contentType: file.type });

  if (uploadError) throw new Error(uploadError.message);

  const { data } = supabase.storage.from('company-logos').getPublicUrl(path);
  // Ajouter un paramètre de version pour invalider le cache navigateur/CDN
  // à chaque nouvel upload (même nom de fichier, contenu différent).
  const publicUrl = `${data.publicUrl}?v=${Date.now()}`;

  const { error: updateError } = await supabase
    .from('companies')
    .update({ logo_url: publicUrl })
    .eq('id', companyId);

  if (updateError) throw new Error(updateError.message);

  return publicUrl;
}

/**
 * Supprime le logo de la company (remet logo_url à null).
 */
export async function removeCompanyLogo(companyId: string): Promise<void> {
  const { error } = await supabase
    .from('companies')
    .update({ logo_url: null })
    .eq('id', companyId);

  if (error) throw new Error(error.message);
}
